import React from 'react';

const RELOAD_FLAG_KEY = 'chunk-reload-at';
const RELOAD_WINDOW_MS = 30_000;

// 테스트 및 재사용을 위해 분리한 복구 로직.
// sessionStorage 접근은 사파리 구형 프라이빗 모드 등에서 throw 할 수 있어 방어.
const readLastReloadAt = (): number => {
  try {
    return Number(sessionStorage.getItem(RELOAD_FLAG_KEY) || 0);
  } catch {
    return 0;
  }
};

const writeLastReloadAt = (at: number): void => {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(at));
  } catch {
    /* 저장 실패 시 새로고침 루프 방지가 약해질 뿐, 동작엔 지장 없음 */
  }
};

/**
 * lazy import 실패(ChunkLoadError) 복구: 최근 30초 내 새로고침한 적이 없으면 1회 새로고침.
 * 이미 새로고침했는데도 실패하면(진짜 네트워크 장애 등) 에러를 그대로 던져
 * 상위 ErrorBoundary(noindex 폴백)가 처리하게 한다.
 */
export const recoverChunkLoadError = <T>(importer: () => Promise<T>): Promise<T> =>
  importer().catch((error) => {
    const now = Date.now();
    if (now - readLastReloadAt() < RELOAD_WINDOW_MS) {
      throw error;
    }
    writeLastReloadAt(now);
    window.location.reload();
    // 새로고침이 완료될 때까지 pending 유지 — Suspense 로딩 화면이 유지되어
    // 에러 화면이 잠깐 깜빡이는 것을 방지 (크롤러가 에러 문구를 색인하는 것도 방지)
    return new Promise<never>(() => undefined);
  });

/**
 * @description
 * React.lazy + 청크 로드 실패 시 1회 자동 새로고침.
 *
 * 배포마다 JS 청크 해시가 바뀌는데, 옛 index.html 을 든 브라우저/검색봇이 라우트를 이동하면
 * 존재하지 않는 청크를 요청해 "Loading chunk N failed" 가 난다. 이 에러 화면이 구글에
 * "Unexpected Application Error!" 제목으로 색인된 사례가 있어(2026-07 확인),
 * 새로고침으로 새 index.html 을 받아 정상 복구한다.
 */
export const lazyWithRetry = <T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> => React.lazy(() => recoverChunkLoadError(importer));
